import { Button } from '@base-ui/react/button';
import { faBookOpen, faFileLines } from '@fortawesome/free-solid-svg-icons';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import styled from 'styled-components';
import api, { queryClient } from '@/api';
import { apiErrorDetail } from '@/api/errorDetail';
import type { components } from '@/api/qraft/schema';
import { ConfirmDialog } from '~/components/ui/ConfirmDialog';
import {
  CourseOutlineLayout,
  courseOf,
  TopicHeader,
} from '~/components/ui/ContentNav';
import { DraftAncestorNotice } from '~/components/ui/DraftAncestorNotice';
import {
  DangerGroup,
  DetailPage,
  EditorLayout,
  EditorMain,
  EditorPanel,
  FormSection,
  PanelGroup,
  PanelLabel,
  SaveGroup,
} from '~/components/ui/Editor';
import { useEditMode, validateEditSearch } from '~/components/ui/editSearch';
import { Field, FieldLabel, FieldValue } from '~/components/ui/FieldDisplay';
import {
  ErrorText,
  FieldHint,
  FormLabel,
  FormRow,
  Select,
  TextInput,
} from '~/components/ui/Form';
import {
  QuizPublishWarning,
  quizPublishMessage,
} from '~/components/ui/QuizLock';
import { richContentCss } from '~/components/ui/RichContent';
import { RichTextEditor } from '~/components/ui/RichTextEditor';
import { FieldGrid, Section, SectionTitle } from '~/components/ui/Section';
import { useUnsavedChanges } from '~/components/ui/useUnsavedChanges';
import {
  clampPosition,
  deletePrompt,
  EditConflictNotice,
  editConflict,
  LiveEditNotice,
  NeedsNewVersion,
  useCourseVersions,
} from '~/components/ui/Versions';
import buttonStyles from '~/styles/button.module.css';

type TopicAdmin = components['schemas']['TopicAdmin'];

export const Route = createFileRoute('/_admin/topics/$topicId')({
  component: TopicDetail,
  validateSearch: validateEditSearch,
});

function TopicDetail() {
  const { topicId } = Route.useParams();
  const topicIdNum = Number(topicId);
  const { edit } = Route.useSearch();
  const { editing, startEditing, stopEditing } = useEditMode(edit === true);

  const admin = api.adminGetTopic.useQuery({ path: { topic_id: topicIdNum } });

  if (admin.isLoading) return <Message>Loading topic...</Message>;
  if (admin.error || !admin.data)
    return <Message $error>Error loading topic</Message>;

  const contentHtml = admin.data.content;

  return (
    <DetailPage>
      <TopicHeader
        topic={admin.data}
        nav={!editing}
        action={
          !editing && (
            <Button className={buttonStyles.Button} onClick={startEditing}>
              Edit
            </Button>
          )
        }
      />

      {editing ? (
        // key remounts the form when switching topics, so its seeded state stays fresh.
        <TopicEditor
          key={admin.data.id}
          topic={admin.data}
          onDone={stopEditing}
        />
      ) : (
        <CourseOutlineLayout
          course={courseOf(admin.data.path)}
          lesson={admin.data.lesson_id}
        >
          <Section>
            <SectionTitle>Overview</SectionTitle>
            <FieldGrid>
              <Field>
                <FieldLabel>ID</FieldLabel>
                <FieldValue>{admin.data.id}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Slug</FieldLabel>
                <FieldValue>{admin.data.slug || '—'}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Order</FieldLabel>
                <FieldValue>{admin.data.order}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Status</FieldLabel>
                <FieldValue>{admin.data.status}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Lesson</FieldLabel>
                <FieldValue>
                  <Link
                    to="/lessons/$lessonId"
                    params={{ lessonId: String(admin.data.lesson_id) }}
                  >
                    #{admin.data.lesson_id}
                  </Link>
                </FieldValue>
              </Field>
            </FieldGrid>
          </Section>

          <Section>
            <SectionTitle>Content</SectionTitle>
            {/* biome-ignore lint/security/noDangerouslySetInnerHtml: rendering trusted topic HTML in admin */}
            <ContentBody dangerouslySetInnerHTML={{ __html: contentHtml }} />
          </Section>
        </CourseOutlineLayout>
      )}
    </DetailPage>
  );
}

function TopicEditor({
  topic,
  onDone,
}: {
  topic: TopicAdmin;
  onDone: () => void;
}) {
  const navigate = useNavigate();
  const [title, setTitle] = useState(topic.title);
  const [slug, setSlug] = useState(topic.slug);
  const [order, setOrder] = useState(String(topic.order));
  const [status, setStatus] = useState(topic.status);
  const [content, setContent] = useState(topic.content);
  const [lessonId, setLessonId] = useState(topic.lesson_id);
  // Null until changed: the field shows where the topic already is.
  const [position, setPosition] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  // A published version's topics keep their place and status; those change in a
  // new version. Wording still edits in place.
  const frozen = topic.version_state !== 'draft';
  const course = courseOf(topic.path);
  const { data: versions } = useCourseVersions(course?.id ?? null);
  const inDraftVersion =
    topic.version_state === 'draft' &&
    Boolean(versions?.some((v) => v.state === 'live'));
  const outline = api.adminGetCourseOutline.useQuery(
    {
      path: { course_id: course?.id ?? 0 },
      query: { version: course?.version ?? 1 },
    },
    { enabled: inDraftVersion && course !== null },
  );

  const lessonTopics =
    outline.data?.lessons.find((l) => l.id === lessonId)?.topics ?? [];
  const currentIndex = lessonTopics.findIndex((t) => t.id === topic.id);
  // In another lesson it goes last unless told otherwise.
  const lastPosition =
    currentIndex >= 0 ? lessonTopics.length : lessonTopics.length + 1;
  const shownPosition =
    position ??
    (outline.data
      ? String(currentIndex >= 0 ? currentIndex + 1 : lastPosition)
      : '');

  const formValues = JSON.stringify([
    title,
    slug,
    order,
    status,
    content,
    lessonId,
    position,
  ]);
  const [savedValues] = useState(formValues);
  const dirty = formValues !== savedValues;
  const {
    allowLeave,
    confirmDiscard,
    dialog: unsavedDialog,
  } = useUnsavedChanges(dirty);

  const invalidateTopic = () => {
    queryClient.invalidateQueries({
      queryKey: api.adminGetTopic.getQueryKey({ path: { topic_id: topic.id } }),
    });
    queryClient.invalidateQueries({
      queryKey: api.topicDetail.getQueryKey({ path: { topic_id: topic.id } }),
    });
    queryClient.invalidateQueries({
      queryKey: api.lessonTopics.getQueryKey({
        path: { lesson_id: topic.lesson_id },
      }),
    });
  };

  const updateMutation = api.adminUpdateTopic.useMutation(undefined, {
    onSuccess: () => {
      allowLeave();
      invalidateTopic();
      onDone();
    },
  });

  const deleteMutation = api.adminDeleteTopic.useMutation(undefined, {
    onSuccess: () => {
      allowLeave();
      queryClient.invalidateQueries({
        queryKey: api.lessonTopics.getQueryKey({
          path: { lesson_id: topic.lesson_id },
        }),
      });
      navigate({ to: '/topics', search: { lesson: topic.lesson_id } });
    },
  });

  const trimmedTitle = title.trim();

  // Publishing stamps the topic for good, so its quiz lock is confirmed first. In
  // a draft of a live course nothing locks until the version itself is published.
  const locksQuiz =
    !inDraftVersion &&
    status === 'published' &&
    topic.status !== 'published' &&
    !topic.quiz_locked;

  // Pinned when editing starts: a refetch (e.g. on tab focus) must not advance it,
  // or a save over someone else's change would slip past the conflict check.
  const [baseRevision] = useState(topic.revision);
  const handleSave = () => {
    if (!trimmedTitle || updateMutation.isPending) return;
    if (locksQuiz && !confirming) {
      setConfirming(true);
      return;
    }
    setConfirming(false);
    updateMutation.mutate({
      path: { topic_id: topic.id },
      body: {
        title: trimmedTitle,
        slug,
        // In a draft, Position places it; a stale order would move it back.
        ...(frozen
          ? {}
          : inDraftVersion
            ? { status }
            : { order: Number(order), status }),
        ...(inDraftVersion && lessonId !== topic.lesson_id
          ? { lesson_id: lessonId }
          : {}),
        ...(inDraftVersion && position ? { position: Number(position) } : {}),
        content,
        expected_revision: baseRevision,
      },
    });
  };

  const handleDelete = () => {
    if (!window.confirm(deletePrompt('topic', topic))) return;
    deleteMutation.mutate({ path: { topic_id: topic.id } });
  };

  const reload = () => {
    allowLeave();
    invalidateTopic();
    onDone();
  };

  return (
    <EditorLayout>
      <EditorMain>
        <LiveEditNotice state={topic.version_state} />
        <FormSection
          tone="content"
          icon={faFileLines}
          title="Details"
          description="How the topic is named and linked."
        >
          <FormRow>
            <FormLabel htmlFor="topic-title">Title</FormLabel>
            <TextInput
              id="topic-title"
              value={title}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setTitle(e.target.value)
              }
            />
          </FormRow>
          <FormRow>
            <FormLabel htmlFor="topic-slug">Slug</FormLabel>
            <TextInput
              id="topic-slug"
              value={slug}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSlug(e.target.value)
              }
            />
          </FormRow>
        </FormSection>

        <FormSection
          tone="feedback"
          icon={faBookOpen}
          title="Content"
          description="What students read in this topic."
        >
          <RichTextEditor
            value={topic.content}
            onChange={setContent}
            ariaLabel="Topic content"
          />
        </FormSection>
      </EditorMain>

      <EditorPanel aria-label="Save and settings">
        <SaveGroup
          heading={<PanelLabel>Topic</PanelLabel>}
          dirty={dirty}
          saving={updateMutation.isPending}
          saveDisabled={!trimmedTitle}
          onSave={handleSave}
          onCancel={() => confirmDiscard(onDone)}
        >
          {!trimmedTitle && <FieldHint>Give the topic a title.</FieldHint>}
          {updateMutation.isError &&
            (editConflict(updateMutation.error) ? (
              <EditConflictNotice
                error={updateMutation.error}
                onReload={reload}
              />
            ) : (
              <ErrorText>
                {apiErrorDetail(updateMutation.error) ??
                  'Could not save changes. Please try again.'}
              </ErrorText>
            ))}
        </SaveGroup>

        {frozen ? (
          <PanelGroup>
            {course && (
              <NeedsNewVersion
                target={{
                  courseId: course.id,
                  version: course.version,
                  item: { entity: 'topic', lineageId: topic.lineage_id },
                }}
              >
                In a published version, a topic's status, lesson and place are
                fixed, and it can't be deleted.
              </NeedsNewVersion>
            )}
            <FieldHint>
              Status: {topic.status} · Order: {topic.order}
            </FieldHint>
          </PanelGroup>
        ) : (
          <PanelGroup>
            <FormRow>
              <FormLabel htmlFor="topic-status">Status</FormLabel>
              <Select
                id="topic-status"
                value={status}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  setStatus(e.target.value as TopicAdmin['status'])
                }
              >
                <option value="draft">draft</option>
                <option value="published">published</option>
              </Select>
              {inDraftVersion && status === 'draft' && (
                <FieldHint>
                  As a draft, this topic stays hidden from students even after
                  this version is published.
                </FieldHint>
              )}
              {locksQuiz && (
                <QuizPublishWarning
                  parent="topic"
                  publishedCount={topic.quiz_published_count}
                  draftCount={topic.quiz_draft_count}
                />
              )}
              <DraftAncestorNotice
                entityLabel="topic"
                ancestors={[
                  { label: 'lesson', status: topic.lesson_status },
                  // In a draft version the course is a draft by definition.
                  ...(inDraftVersion
                    ? []
                    : [{ label: 'course', status: topic.course_status }]),
                  { label: 'series', status: topic.series_status },
                ]}
              />
            </FormRow>
            {inDraftVersion ? (
              <>
                <FormRow>
                  <FormLabel htmlFor="topic-lesson">Lesson</FormLabel>
                  <Select
                    id="topic-lesson"
                    value={lessonId}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      setLessonId(Number(e.target.value));
                      // Its place in the old lesson means nothing in the new one.
                      setPosition(null);
                    }}
                  >
                    {(outline.data?.lessons ?? []).map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.title}
                      </option>
                    ))}
                  </Select>
                </FormRow>
                <FormRow>
                  <FormLabel htmlFor="topic-position">
                    Position in lesson
                  </FormLabel>
                  <TextInput
                    id="topic-position"
                    type="number"
                    min={1}
                    max={lastPosition}
                    value={shownPosition}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setPosition(e.target.value)
                    }
                    onBlur={() =>
                      position !== null &&
                      setPosition(clampPosition(position, lastPosition))
                    }
                  />
                </FormRow>
              </>
            ) : (
              <FormRow>
                <FormLabel htmlFor="topic-order">Order</FormLabel>
                <TextInput
                  id="topic-order"
                  type="number"
                  value={order}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setOrder(e.target.value)
                  }
                />
              </FormRow>
            )}
          </PanelGroup>
        )}

        {!frozen && (
          <DangerGroup>
            <Button
              className={`${buttonStyles.Button} ${buttonStyles.Danger}`}
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete topic'}
            </Button>
            {deleteMutation.isError && (
              <ErrorText>
                {apiErrorDetail(deleteMutation.error) ??
                  'Could not delete the topic. Please try again.'}
              </ErrorText>
            )}
          </DangerGroup>
        )}
      </EditorPanel>

      <ConfirmDialog
        open={confirming}
        title="This can't be undone"
        confirmLabel="Publish"
        onConfirm={handleSave}
        onCancel={() => setConfirming(false)}
      >
        <p>
          {quizPublishMessage({
            parent: 'topic',
            publishedCount: topic.quiz_published_count,
            draftCount: topic.quiz_draft_count,
          })}
        </p>
        <p>Returning the topic to draft later won't unlock it.</p>
      </ConfirmDialog>
      {unsavedDialog}
    </EditorLayout>
  );
}

const ContentBody = styled.div`
  ${richContentCss}
  padding: var(--inner-padding);
`;

const Message = styled.div<{ $error?: boolean }>`
  padding: 2rem;
  text-align: center;
  color: ${(p) => (p.$error ? 'var(--error)' : 'var(--grey-600)')};
`;
